import React, { useState, useEffect, useCallback } from 'react';

import { useDictionary, useViewport, usePage } from '@/src/app/hooks';
import { ErrorMessage, Loading, ResourceBoxInner, type ResourceHeaderDTO } from '@/src/ui';
import { filterAssessmentsData, filterLanguageSetting } from '@/src/utils';
import { apiRequest } from '@/src/services';
import './css/ResourceBoxes.css';

interface AssessmentBoxesProps {
    grade: string;
    showTitle?: boolean;
    preventDataLoad?: boolean;
    preloadedAssessmentsData?: ResourceHeaderDTO[];
}

const AssessmentBoxes: React.FC<AssessmentBoxesProps> = ({
    grade,
    showTitle,
    preventDataLoad = false,
    preloadedAssessmentsData = null,
}) => {
    const { translate } = useDictionary();
    const { isMobile } = useViewport();
    const { pageLanguage } = usePage();

    const [assessmentsData, setAssessmentsData] = useState<ResourceHeaderDTO[] | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async function fetchData() {
            if (preventDataLoad) {
                return;
            }
            setError('');
            setLoading(true);
            setAssessmentsData(null);
            try {
                const res = await apiRequest<ResourceHeaderDTO[]>(
                    'GET',
                    `dbo/assessments?grade=${grade}&language=${filterLanguageSetting(grade, pageLanguage)}`
                );
                setAssessmentsData(filterAssessmentsData(res.data, grade, pageLanguage));
            } catch {
                setError('Failed to fetch data');
            } finally {
                setLoading(false);
            }
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grade, pageLanguage]);

    const renderColumn = useCallback(
        (colNo: number) => {
            const assessmentsDataToUse = preventDataLoad
                ? preloadedAssessmentsData
                : assessmentsData;
            if (!assessmentsDataToUse) {
                return null;
            }
            const boxes = assessmentsDataToUse?.filter((box) => box.columnNumber === colNo) || [];
            return (
                <>
                    {boxes.map((box) => {
                        if (box.key === 'activitiesByStandardBox') {
                            return null;
                        }
                        return (
                            <div
                                key={`assessments-box-c${colNo}-t${box.id}`}
                                className={`resources-box background pd-G${grade}-primary`}
                            >
                                <div className="tile-headings">{box.title}</div>
                                {box.items.map((item) => {
                                    const anchorId = `assessments-container-c${colNo}-${item.id}`;
                                    return (
                                        <ResourceBoxInner
                                            key={anchorId}
                                            id={anchorId}
                                            item={item}
                                            grade={grade}
                                            namespace="assessments"
                                        />
                                    );
                                })}
                            </div>
                        );
                    })}
                </>
            );
        },
        [assessmentsData, grade, preloadedAssessmentsData, preventDataLoad]
    );

    const title = error ? '' : `${translate('Grade')} ${grade} ${translate('Assessments')}`;

    return (
        <>
            {showTitle && (
                <div id="assessmentsTitle" className="text pd-primary">
                    <h2 id="assessmentsTitle-text">{title}</h2>
                </div>
            )}
            <ErrorMessage error={error} />
            {loading ? (
                <Loading />
            ) : (
                <div id="assessments-container" className="container resources-container">
                    <div className={`${isMobile ? 'col' : 'row'}`}>
                        <div className={`resources-column col ${isMobile ? 'mb-4' : ''}`}>
                            {renderColumn(1)}
                        </div>
                        <div className={`resources-column col ${isMobile ? 'mb-4' : ''}`}>
                            {renderColumn(2)}
                        </div>
                        <div className={`resources-column col ${isMobile ? 'mb-4' : ''}`}>
                            {renderColumn(3)}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AssessmentBoxes;
